/**
 * Test fixtures for the Content Themes & Hooks aggregation.
 *
 * TEST-ONLY. The tab derives its data from real post captions via
 * lib/contentClassifier.js; this file exists purely to exercise the merge and
 * reconciliation rules against a known, hand-checkable dataset.
 *
 * IMPORTANT: only the creator roster is hand-written. Every theme and hook
 * aggregate is DERIVED from those creators' breakdowns, so the totals always
 * reconcile: a theme row can never claim more creators than the table lists,
 * and the "posts analysed" figure always equals the sum of the theme counts.
 * Hand-writing both halves is what let them drift apart before.
 */

// Each creator's theme_breakdown and hook_breakdown must sum to post_count —
// every post has exactly one theme and one hook.
const IG_CREATORS = [
  {
    creator_id: 'sarahj_style',
    name: 'Sarah Johnson',
    handle: 'sarahj_style',
    followers: 850000,
    image_url: 'https://picsum.photos/seed/creator1/80/80',
    category: 'Food',
    avg_engagement_rate: 6.9,
    total_views: 210000,
    theme_breakdown: [
      { id: 'morning-routine', label: 'Morning Routine', post_count: 9 },
      { id: 'behind-the-scenes', label: 'Behind The Scenes', post_count: 5 },
      { id: 'product-review', label: 'Product Review', post_count: 3 },
    ],
    hook_breakdown: [
      { id: 'question-hook', label: 'Question Hook', post_count: 7 },
      { id: 'story-open', label: 'Story Open', post_count: 6 },
      { id: 'listicle', label: 'Listicle', post_count: 4 },
    ],
  },
  {
    creator_id: 'priya.cooks',
    name: 'Priya Nair',
    handle: 'priya.cooks',
    followers: 1250000,
    image_url: 'https://picsum.photos/seed/creator2/80/80',
    category: 'Food',
    avg_engagement_rate: 9.4,
    total_views: 812000,
    theme_breakdown: [
      { id: 'recipe-hack', label: 'Recipe Hack', post_count: 11 },
      { id: 'morning-routine', label: 'Morning Routine', post_count: 7 },
    ],
    hook_breakdown: [
      { id: 'listicle', label: 'Listicle', post_count: 9 },
      { id: 'question-hook', label: 'Question Hook', post_count: 5 },
      { id: 'bold-claim', label: 'Bold Claim', post_count: 4 },
    ],
  },
  {
    creator_id: 'marcus.lifts',
    name: 'Marcus Chen',
    handle: 'marcus.lifts',
    followers: 320000,
    image_url: 'https://picsum.photos/seed/creator3/80/80',
    category: 'Fitness',
    avg_engagement_rate: 5.1,
    total_views: 148000,
    theme_breakdown: [
      { id: 'behind-the-scenes', label: 'Behind The Scenes', post_count: 6 },
      { id: 'morning-routine', label: 'Morning Routine', post_count: 4 },
    ],
    hook_breakdown: [
      { id: 'question-hook', label: 'Question Hook', post_count: 6 },
      { id: 'story-open', label: 'Story Open', post_count: 4 },
    ],
  },
  {
    creator_id: 'anita.reviews',
    name: 'Anita Rao',
    handle: 'anita.reviews',
    followers: 74000,
    image_url: 'https://picsum.photos/seed/creator4/80/80',
    category: 'Lifestyle',
    avg_engagement_rate: 11.2,
    total_views: 96000,
    theme_breakdown: [
      { id: 'product-review', label: 'Product Review', post_count: 8 },
      { id: 'recipe-hack', label: 'Recipe Hack', post_count: 2 },
    ],
    hook_breakdown: [
      { id: 'bold-claim', label: 'Bold Claim', post_count: 7 },
      { id: 'listicle', label: 'Listicle', post_count: 3 },
    ],
  },
  {
    creator_id: 'devroams',
    name: 'Dev Malhotra',
    handle: 'devroams',
    followers: 15000,
    image_url: 'https://picsum.photos/seed/creator5/80/80',
    category: 'Travel',
    avg_engagement_rate: 4.7,
    total_views: 41000,
    theme_breakdown: [{ id: 'travel-moment', label: 'Travel Moment', post_count: 5 }],
    hook_breakdown: [
      { id: 'story-open', label: 'Story Open', post_count: 4 },
      { id: 'question-hook', label: 'Question Hook', post_count: 1 },
    ],
  },
  {
    creator_id: 'kavya.home',
    name: 'Kavya Iyer',
    handle: 'kavya.home',
    followers: 4200,
    image_url: 'https://picsum.photos/seed/creator6/80/80',
    category: 'Home Decor',
    avg_engagement_rate: 3.2,
    total_views: 9800,
    theme_breakdown: [{ id: 'behind-the-scenes', label: 'Behind The Scenes', post_count: 4 }],
    hook_breakdown: [
      { id: 'story-open', label: 'Story Open', post_count: 3 },
      { id: 'listicle', label: 'Listicle', post_count: 1 },
    ],
  },
];

const HOOK_DESCRIPTIONS = {
  'question-hook': 'Opens with a direct question to the viewer',
  'bold-claim': 'Leads with a strong, contrarian statement',
  listicle: 'Promises a numbered set of tips or items',
  'story-open': 'Begins mid-anecdote to pull the viewer in',
};

const EXAMPLE_POSTS = {
  'morning-routine': [
    {
      id: '18012345',
      shortcode: 'Cx1AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/theme1a/200/200',
      caption_snippet: 'Starting my day right with a cup that actually wakes me up',
      engagement_rate: 8.1,
      view_count: 52000,
      creator_id: 'sarahj_style',
    },
    {
      id: '18012346',
      shortcode: 'Cx2AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/theme1b/200/200',
      caption_snippet: '6am and the kettle is already on. Who else?',
      engagement_rate: 7.2,
      view_count: 41000,
      creator_id: 'priya.cooks',
    },
  ],
  'recipe-hack': [
    {
      id: '18012347',
      shortcode: 'Cx3AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/theme2a/200/200',
      caption_snippet: '3-ingredient cold brew you can make tonight',
      engagement_rate: 11.4,
      view_count: 132000,
      creator_id: 'priya.cooks',
    },
  ],
  'behind-the-scenes': [
    {
      id: '18012348',
      shortcode: 'Cx4AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/theme3a/200/200',
      caption_snippet: 'What a shoot day actually looks like',
      engagement_rate: 5.6,
      view_count: 38000,
      creator_id: 'marcus.lifts',
    },
  ],
  'product-review': [
    {
      id: '18012349',
      shortcode: 'Cx5AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/theme4a/200/200',
      caption_snippet: 'Tested for 30 days — here is the honest verdict',
      engagement_rate: 4.4,
      view_count: 29000,
      creator_id: 'anita.reviews',
    },
  ],
  'travel-moment': [
    {
      id: '18012350',
      shortcode: 'Cx6AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/theme5a/200/200',
      caption_snippet: 'Sunrise at 12,000 ft and one very good flask',
      engagement_rate: 5.1,
      view_count: 34000,
      creator_id: 'devroams',
    },
  ],
  'question-hook': [
    {
      id: '18012351',
      shortcode: 'Cx7AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/hook1a/200/200',
      caption_snippet: 'Ever wondered why your coffee tastes bitter?',
      engagement_rate: 9.2,
      view_count: 88000,
      creator_id: 'marcus.lifts',
    },
  ],
  'bold-claim': [
    {
      id: '18012352',
      shortcode: 'Cx8AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/hook2a/200/200',
      caption_snippet: 'You have been storing your beans wrong this whole time',
      engagement_rate: 12.1,
      view_count: 154000,
      creator_id: 'anita.reviews',
    },
  ],
  listicle: [
    {
      id: '18012353',
      shortcode: 'Cx9AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/hook3a/200/200',
      caption_snippet: '5 ways to upgrade your morning cup',
      engagement_rate: 6.4,
      view_count: 47000,
      creator_id: 'priya.cooks',
    },
  ],
  'story-open': [
    {
      id: '18012354',
      shortcode: 'Cy1AbCdEf',
      thumbnail_url: 'https://picsum.photos/seed/hook4a/200/200',
      caption_snippet: 'I missed my flight, and then this happened',
      engagement_rate: 5.9,
      view_count: 36000,
      creator_id: 'devroams',
    },
  ],
};

const sumBreakdown = (breakdown) =>
  breakdown.reduce((sum, entry) => sum + entry.post_count, 0);

/**
 * Roll a creator roster up into theme/hook aggregates. Every figure here is a
 * function of the roster, which is what keeps the headline numbers and the
 * table in agreement.
 */
const deriveAggregates = (creators, breakdownKey) => {
  const acc = new Map();

  creators.forEach((creator) => {
    creator[breakdownKey].forEach((entry) => {
      const current = acc.get(entry.id) || {
        id: entry.id,
        label: entry.label,
        post_count: 0,
        creators_count: 0,
        total_views: 0,
        _engagementWeighted: 0,
      };

      const share = entry.post_count / sumBreakdown(creator[breakdownKey]);
      current.post_count += entry.post_count;
      current.creators_count += 1;
      current.total_views += Math.round(creator.total_views * share);
      current._engagementWeighted += creator.avg_engagement_rate * entry.post_count;
      acc.set(entry.id, current);
    });
  });

  const totalPosts = Array.from(acc.values()).reduce((sum, e) => sum + e.post_count, 0);

  return Array.from(acc.values())
    .map(({ _engagementWeighted, ...entry }) => ({
      ...entry,
      avg_engagement_rate: Number((_engagementWeighted / entry.post_count).toFixed(2)),
      share_of_posts: Number(((entry.post_count / totalPosts) * 100).toFixed(1)),
      total_likes: Math.round(entry.total_views * 0.05),
      total_comments: Math.round(entry.total_views * 0.002),
      description: HOOK_DESCRIPTIONS[entry.id],
      top_creators: creators
        .filter((c) => c[breakdownKey].some((b) => b.id === entry.id))
        .map((c) => ({
          creator_id: c.creator_id,
          name: c.name,
          post_count: c[breakdownKey].find((b) => b.id === entry.id).post_count,
        }))
        .sort((a, b) => b.post_count - a.post_count)
        .slice(0, 3),
      example_posts: EXAMPLE_POSTS[entry.id] || [],
    }))
    .sort((a, b) => b.post_count - a.post_count);
};

const buildPayload = (creators, platform) => {
  // The roster drives everything, so post_count is derived rather than stated.
  const withTotals = creators.map((c) => ({
    ...c,
    post_count: sumBreakdown(c.theme_breakdown),
    top_theme: [...c.theme_breakdown].sort((a, b) => b.post_count - a.post_count)[0],
    top_hook: [...c.hook_breakdown].sort((a, b) => b.post_count - a.post_count)[0],
  }));

  // Kavya deliberately keeps no precomputed top_theme/top_hook, so the UI's
  // fallback to *_breakdown stays exercised.
  const roster = withTotals.map((c) =>
    c.creator_id === 'kavya.home'
      ? { ...c, top_theme: undefined, top_hook: undefined }
      : c,
  );

  return {
    platform,
    total_posts_analyzed: roster.reduce((sum, c) => sum + c.post_count, 0),
    themes: deriveAggregates(roster, 'theme_breakdown'),
    hooks: deriveAggregates(roster, 'hook_breakdown'),
    creators: roster,
  };
};

export const CONTENT_THEMES_FIXTURE_INSTAGRAM = buildPayload(IG_CREATORS, 'instagram');
export const CONTENT_THEMES_FIXTURE_YOUTUBE = buildPayload(
  IG_CREATORS.slice(0, 4),
  'youtube',
);

/**
 * Each brand in a report gets a different slice of the roster, so the per-brand
 * split bars show real variation instead of dead-even segments. Varying the
 * roster (rather than scaling the aggregates) keeps every derived total
 * internally consistent for each brand.
 */
const rosterForBrand = (creators, brandHandle) => {
  const seed = String(brandHandle || '')
    .split('')
    .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);

  const selected = creators.filter((_, i) => (i + seed) % 3 !== 0);
  const roster = selected.length > 0 ? selected : creators;

  // Nudge each creator's volume so two brands sharing a creator still differ.
  const factor = 0.6 + ((seed % 9) / 8) * 0.8;
  const scale = (breakdown) =>
    breakdown.map((b) => ({ ...b, post_count: Math.max(1, Math.round(b.post_count * factor)) }));

  return roster.map((c) => {
    const themes = scale(c.theme_breakdown);
    const hooks = scale(c.hook_breakdown);

    // Every post carries exactly one theme and one hook, so the two breakdowns
    // must total the same. Rounding each independently drifts them apart, which
    // would let the hooks column claim more posts than were analysed — absorb
    // the difference into the creator's largest hook.
    const drift = sumBreakdown(themes) - sumBreakdown(hooks);
    if (drift !== 0) {
      const largest = hooks.reduce((best, h) => (h.post_count > best.post_count ? h : best), hooks[0]);
      largest.post_count = Math.max(1, largest.post_count + drift);
    }

    return {
      ...c,
      theme_breakdown: themes,
      hook_breakdown: hooks,
      total_views: Math.round(c.total_views * factor),
    };
  });
};

export const getContentThemesFixture = (platform, brandHandle) => {
  const isYouTube = platform === 'yt' || platform === 'youtube';
  const base = isYouTube ? IG_CREATORS.slice(0, 4) : IG_CREATORS;

  if (!brandHandle) {
    return isYouTube ? CONTENT_THEMES_FIXTURE_YOUTUBE : CONTENT_THEMES_FIXTURE_INSTAGRAM;
  }

  return {
    ...buildPayload(rosterForBrand(base, brandHandle), isYouTube ? 'youtube' : 'instagram'),
    brand: brandHandle,
  };
};
